/**
 * @module utils/mount_header_favorite_count
 * ヘッダーのお気に入り件数バッジをマウントするモジュール
 *
 * 設計方針・実装詳細: docs/02_architecture/adr/20260722-avoid-global-state-management.md
 */

import { createApp, h } from 'vue'
import HeaderFavoriteCount from '@/javascripts/common/header_favorite_count.vue'

/** マウント先要素ID一覧(PC/SP) */
const MOUNT_TARGET_IDS = ['header_favorite_count_pc', 'header_favorite_count_sp']

/** Vueインスタンス保存用プロパティ名（二重マウント防止） */
const MOUNTED_INSTANCE_PROPERTY = '__headerFavoriteCountVueInstance'

/**
 * 1つの要素にHeaderFavoriteCountをマウント
 *
 * @param {HTMLElement} element マウント先要素
 * @returns {import('vue').App} Vueアプリインスタンス
 */
function mountInto(element) {
  // 親要素のid/classを維持するため、子要素にマウント
  element.textContent = '';
  const mountPoint = document.createElement('span');
  element.appendChild(mountPoint);

  const app = createApp({
    render: () => h(HeaderFavoriteCount, { ref: 'headerFavoriteCount' })
  });

  const instance = app.mount(mountPoint);
  instance._app = app; // unmount用に保持
  return instance;
}

/**
 * ヘッダーのお気に入り件数バッジ(PC/SP)をマウント
 *
 * 既にマウント済みの場合は既存インスタンスを再利用する（二重マウント防止）
 *
 * @returns {() => void} バッジを更新する関数。Cookie `_fp` 更新後に呼び出すことで、PC/SP双方のヘッダー表示を同期
 */
export function mountHeaderFavoriteCount() {
  const instances = MOUNT_TARGET_IDS
    .map(id => document.getElementById(id))
    .filter(element => element)
    .map(element => {
      if(!element[MOUNTED_INSTANCE_PROPERTY]) {
        element[MOUNTED_INSTANCE_PROPERTY] = mountInto(element);
      }
      return element[MOUNTED_INSTANCE_PROPERTY];
    });

  return () => {
    instances.forEach(instance => {
      const headerFavoriteCount = instance.$refs?.headerFavoriteCount;
      if(!headerFavoriteCount) return;

      headerFavoriteCount.refresh();
    });
  };
}
